import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, Outlet, useParams, useSearchParams } from "react-router";

import { DataTable } from "../../components/DataTable";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingBlock,
  PageHeader,
  TextField,
} from "../../components/ui";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import { formatDate, formatDateTime, todayInZone } from "../../lib/format";
import type { Option, Page } from "../../lib/types";
import { StorageBanner } from "../admin/AdminPages";
import { StatusBadge } from "../orders/OrderBits";
import { Detail } from "../orders/OrdersPages";
import type { OrderDetail, OrderSummary } from "../orders/types";
import { OrderFileList } from "./OrderFiles";
import { AdminOrderEditor, CompleteOrderButton } from "./StaffOrderActions";

export function StaffLayout() {
  const { user } = useAuth();
  return (
    <div>
      {user?.is_admin && (
        <div className="print:hidden">
          <StorageBanner />
        </div>
      )}
      <Outlet />
    </div>
  );
}

function urgency(o: OrderSummary): { label: string; tone: "danger" | "warn" | "neutral" } | null {
  if (o.production_status === "completed" || o.production_status === "cancelled") return null;
  const hours = (new Date(o.effective_due_at).getTime() - Date.now()) / 3_600_000;
  if (hours < 0) return { label: "Overdue", tone: "danger" };
  if (hours < 24) return { label: "Due within 24 hours", tone: "warn" };
  return null;
}

const linkBtn =
  "inline-flex min-h-11 items-center justify-center rounded-md border border-brand-600 bg-white px-4 text-sm font-semibold text-brand-700 hover:bg-brand-50";

function useQueueQuery(kind: "open" | "closed", q: string, day: string, page: number) {
  return useQuery({
    queryKey: ["staff-orders", kind, q, day, page],
    queryFn: () => {
      const p = new URLSearchParams({
        page: String(page),
        page_size: kind === "open" ? "100" : "25",
        sort: "due",
        direction: kind === "open" ? "asc" : "desc",
        include_files: "true",
      });
      if (kind === "closed") {
        p.append("status", "completed");
        p.append("status", "cancelled");
      }
      if (q) p.set("q", q);
      if (day) {
        p.set("due_from", day);
        p.set("due_to", day);
      }
      return api.get<Page<OrderSummary>>(`/staff/orders?${p}`);
    },
    refetchInterval: 60_000,
  });
}

/** Orders grouped under a heading per due day. */
function OrdersByDay({ orders, tz }: { orders: OrderSummary[]; tz: string }) {
  const days = new Map<string, OrderSummary[]>();
  for (const o of orders) {
    const key = formatDateTime(o.effective_due_at, tz).split(",").slice(0, 3).join(",");
    days.set(key, [...(days.get(key) ?? []), o]);
  }
  return (
    <div className="flex flex-col gap-6">
      {[...days.entries()].map(([day, list]) => (
        <section key={day} aria-label={`Due ${day}`}>
          <h3 className="mb-2 border-b border-line pb-1 font-bold text-ink-muted">
            Due {day} <span className="font-normal">({list.length})</span>
          </h3>
          <ul className="flex flex-col gap-3">
            {list.map((o) => (
              <QueueOrderCard key={o.id} order={o} tz={tz} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function QueueOrderCard({ order: o, tz }: { order: OrderSummary; tz: string }) {
  const u = urgency(o);
  return (
    <li
      className="rounded-lg border border-line bg-surface p-4 shadow-sm"
      aria-label={`Order ${o.order_number}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2">
            <Link
              className="text-lg font-bold text-brand-700 underline"
              to={`/staff/orders/${o.id}`}
            >
              {o.order_number}
            </Link>
            <StatusBadge status={o.production_status} />
            {u && <Badge tone={u.tone}>{u.label}</Badge>}
          </p>
          <p className="mt-1">
            <span className="font-semibold">{o.customer_name}</span>
            {o.company_name && <span className="text-ink-muted"> · {o.company_name}</span>}
            <span className="text-ink-muted"> · {o.project_name_number}</span>
          </p>
          <p className="text-sm text-ink-muted">
            Due {formatDateTime(o.effective_due_at, tz)} · submitted{" "}
            {formatDate(o.submitted_at, tz)}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <a
            className={linkBtn}
            href={`/staff/orders/${o.id}?print=1`}
            target="_blank"
            rel="noreferrer"
            aria-label={`Print order ${o.order_number}`}
          >
            Print
          </a>
          <CompleteOrderButton order={o} />
        </div>
      </div>
      <div className="mt-3 border-t border-line pt-2">
        <OrderFileList orderId={o.id} files={o.files} />
      </div>
    </li>
  );
}

export function StaffQueuePage() {
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const today = todayInZone(tz);
  const yesterday = new Date(Date.parse(`${today}T12:00:00Z`) - 86_400_000)
    .toISOString()
    .slice(0, 10);
  const [q, setQ] = useState("");
  const [day, setDay] = useState("");
  const [closedPage, setClosedPage] = useState(1);
  const open = useQueueQuery("open", q, day, 1);
  const closed = useQueueQuery("closed", q, day, closedPage);
  const closedPages = Math.max(1, Math.ceil((closed.data?.total ?? 0) / 25));
  const reset = () => setClosedPage(1);
  return (
    <div>
      <PageHeader
        title="Production queue"
        description="Orders by due day. Mark an order completed and it moves to Closed orders."
      />
      <div className="mb-6 grid items-end gap-3 sm:grid-cols-[2fr_1fr_auto]">
        <TextField
          label="Search"
          placeholder="Order, project, customer or company"
          value={q}
          onChange={(e) => {
            reset();
            setQ(e.target.value);
          }}
        />
        <TextField
          label="Date"
          type="date"
          value={day}
          onChange={(e) => {
            reset();
            setDay(e.target.value);
          }}
        />
        <div className="flex flex-wrap gap-2 sm:mb-0">
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              setDay(yesterday);
            }}
          >
            Yesterday
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              setDay(today);
            }}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            disabled={!day}
            onClick={() => {
              reset();
              setDay("");
            }}
          >
            All dates
          </Button>
        </div>
      </div>

      <section aria-labelledby="open-orders" className="mb-10">
        <h2 id="open-orders" className="mb-3 text-xl font-bold">
          Open orders {open.data && <span className="text-ink-muted">({open.data.total})</span>}
        </h2>
        {open.isLoading ? (
          <LoadingBlock />
        ) : open.error ? (
          <Alert tone="error">{errorMessage(open.error)}</Alert>
        ) : open.data?.items.length ? (
          <>
            <OrdersByDay orders={open.data.items} tz={tz} />
            {open.data.total > open.data.items.length && (
              <p className="mt-3 text-sm text-ink-muted">
                Showing the first {open.data.items.length} of {open.data.total}. Choose a date to
                see the rest.
              </p>
            )}
          </>
        ) : (
          <EmptyState title={day ? "No open orders due on this date" : "No open orders"} />
        )}
      </section>

      <section aria-labelledby="closed-orders">
        <h2 id="closed-orders" className="mb-3 text-xl font-bold">
          Closed orders{" "}
          {closed.data && <span className="text-ink-muted">({closed.data.total})</span>}
        </h2>
        {closed.isLoading ? (
          <LoadingBlock />
        ) : closed.error ? (
          <Alert tone="error">{errorMessage(closed.error)}</Alert>
        ) : closed.data?.items.length ? (
          <>
            <OrdersByDay orders={closed.data.items} tz={tz} />
            {closedPages > 1 && (
              <nav aria-label="Closed orders pages" className="mt-4 flex items-center gap-3">
                <Button
                  variant="secondary"
                  disabled={closedPage <= 1}
                  onClick={() => setClosedPage((p) => p - 1)}
                >
                  Newer
                </Button>
                <span className="text-sm">
                  Page {closedPage} of {closedPages}
                </span>
                <Button
                  variant="secondary"
                  disabled={closedPage >= closedPages}
                  onClick={() => setClosedPage((p) => p + 1)}
                >
                  Older
                </Button>
              </nav>
            )}
          </>
        ) : (
          <EmptyState title={day ? "No closed orders due on this date" : "No closed orders"} />
        )}
      </section>
    </div>
  );
}

function BackToQueue() {
  return (
    <Link
      to="/staff"
      className="inline-flex min-h-11 items-center font-semibold text-brand-700 underline print:hidden"
    >
      ← Back to queue
    </Link>
  );
}

export function StaffOrderPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const [editing, setEditing] = useState(false);
  const query = useQuery({
    queryKey: ["staff-order", id],
    queryFn: () => api.get<OrderDetail>(`/staff/orders/${id}`),
  });
  const printNow = params.get("print") === "1" && !!query.data && !!config;
  useEffect(() => {
    if (printNow) window.setTimeout(() => window.print(), 300);
  }, [printNow]);
  if (query.isLoading) return <LoadingBlock />;
  if (!query.data) return <Alert tone="error">{errorMessage(query.error)}</Alert>;
  const order = query.data;
  const o = config?.options;
  const lbl = (opts: Option[] | undefined, v: string) =>
    opts?.find((x) => x.value === v)?.label ?? v;
  return (
    <>
      <OrderPrintSheet order={order} lbl={lbl} tz={tz} />
      <div className="flex flex-col gap-4 print:hidden">
        <BackToQueue />
        <PageHeader
          title={`Order ${order.order_number}`}
          description={
            <span className="flex flex-wrap items-center gap-2">
              <StatusBadge status={order.production_status} /> {order.customer_name}
              {order.company_name && ` · ${order.company_name}`} · submitted{" "}
              {formatDateTime(order.submitted_at, tz)}
            </span>
          }
          actions={
            <span className="flex flex-wrap gap-2 print:hidden">
              <Button variant="secondary" onClick={() => window.print()}>
                Print
              </Button>
              {user?.is_admin && !editing && order.production_status !== "cancelled" && (
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Edit order
                </Button>
              )}
              <CompleteOrderButton order={order} />
            </span>
          }
        />
        {editing ? (
          <AdminOrderEditor order={order} onDone={() => setEditing(false)} />
        ) : (
          <>
            <Card>
              <h2 className="mb-3 text-lg font-bold">Order details</h2>
              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
                <Detail label="Due">{formatDateTime(order.effective_due_at, tz)}</Detail>
                <Detail label="Requested due">{formatDateTime(order.requested_due_at, tz)}</Detail>
                <Detail label="Project">{order.project_name_number}</Detail>
                <Detail label="Department">{lbl(o?.departments, order.department)}</Detail>
                <Detail label="Size">{lbl(o?.print_sizes, order.print_size)}</Detail>
                <Detail label="Binding">
                  {order.binding === "other"
                    ? `Other: ${order.binding_other}`
                    : lbl(o?.bindings, order.binding)}
                </Detail>
                <Detail label="Originals">{order.originals_count}</Detail>
                <Detail label="Printed sets">{order.printed_sets}</Detail>
                <Detail label="Payment">{lbl(o?.payment_methods, order.payment_method)}</Detail>
                <div className="sm:col-span-3">
                  <Detail label="Notes">
                    <span className="whitespace-pre-wrap">{order.notes}</span>
                  </Detail>
                </div>
              </dl>
            </Card>
            <Card>
              <h2 className="mb-3 text-lg font-bold">Customer and delivery</h2>
              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
                <Detail label="Customer">{order.customer_name}</Detail>
                <Detail label="Company">{order.customer_company_name}</Detail>
                <Detail label="Submitted by">{order.submitted_by_name}</Detail>
                <Detail label="Phone">{order.customer_phone}</Detail>
                <Detail label="Email">{order.customer_email}</Detail>
                <Detail label="Address">
                  {order.customer_street}, {order.customer_city}, {order.customer_state}{" "}
                  {order.customer_zip}
                </Detail>
                <Detail label="Delivery">{lbl(o?.delivery_methods, order.delivery_method)}</Detail>
                {order.ship_street && (
                  <Detail label="Deliver to">
                    {order.ship_recipient}, {order.ship_street}, {order.ship_city},{" "}
                    {order.ship_state} {order.ship_zip}
                  </Detail>
                )}
                {order.ship_instructions && (
                  <Detail label="Instructions">{order.ship_instructions}</Detail>
                )}
              </dl>
            </Card>
            <Card>
              <h2 className="mb-3 text-lg font-bold">Files ({order.files.length})</h2>
              {order.file_expires_at && (
                <p className="mb-2 text-sm text-ink-muted">
                  These files will be removed on {formatDate(order.file_expires_at, tz)}.
                </p>
              )}
              <OrderFileList orderId={order.id} files={order.files} />
            </Card>
          </>
        )}
        <BackToQueue />
      </div>
    </>
  );
}

const PRINT_MAX_FILES = 20;

/** The printed order: always one 8.5 x 11 page (long notes and file lists are shortened). */
function OrderPrintSheet({
  order,
  lbl,
  tz,
}: {
  order: OrderDetail;
  lbl: (opts: Option[] | undefined, v: string) => string;
  tz: string;
}) {
  const o = useConfig().data?.options;
  const files = order.files.slice(0, PRINT_MAX_FILES);
  const more = order.files.length - files.length;
  const row = (label: string, value: React.ReactNode) => (
    <div className="flex gap-2 border-b border-gray-300 py-[3px]">
      <dt className="w-28 shrink-0 font-semibold">{label}</dt>
      <dd className="min-w-0 break-words">{value || "—"}</dd>
    </div>
  );
  return (
    <div className="print-sheet hidden text-[10pt] leading-snug text-black print:block">
      <div className="flex items-end justify-between border-b-2 border-black pb-1">
        <div>
          <p className="text-[9pt]">Bill's Blueprint · Print order</p>
          <h1 className="text-[18pt] font-bold">Order {order.order_number}</h1>
        </div>
        <div className="text-right">
          <p className="text-[12pt] font-bold">Due {formatDateTime(order.effective_due_at, tz)}</p>
          <p className="text-[9pt]">Submitted {formatDateTime(order.submitted_at, tz)}</p>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-6">
        <dl>
          <h2 className="mb-1 text-[11pt] font-bold">Customer</h2>
          {row("Name", order.customer_name)}
          {row("Company", order.customer_company_name)}
          {row("Phone", order.customer_phone)}
          {row("Email", order.customer_email)}
          {row(
            "Address",
            `${order.customer_street}, ${order.customer_city}, ${order.customer_state} ${order.customer_zip}`,
          )}
          {row("Delivery", lbl(o?.delivery_methods, order.delivery_method))}
          {order.ship_street &&
            row(
              "Deliver to",
              `${order.ship_recipient}, ${order.ship_street}, ${order.ship_city}, ${order.ship_state} ${order.ship_zip}`,
            )}
          {order.ship_instructions && row("Instructions", order.ship_instructions)}
        </dl>
        <dl>
          <h2 className="mb-1 text-[11pt] font-bold">Job</h2>
          {row("Project", order.project_name_number)}
          {row("Department", lbl(o?.departments, order.department))}
          {row("Size", lbl(o?.print_sizes, order.print_size))}
          {row("Originals", order.originals_count)}
          {row("Printed sets", order.printed_sets)}
          {row(
            "Binding",
            order.binding === "other"
              ? `Other: ${order.binding_other}`
              : lbl(o?.bindings, order.binding),
          )}
          {row("Payment", lbl(o?.payment_methods, order.payment_method))}
        </dl>
      </div>
      <h2 className="mt-3 text-[11pt] font-bold">Notes</h2>
      <p className="line-clamp-6 min-h-[1.5em] whitespace-pre-wrap border border-gray-400 p-1.5">
        {order.notes || "None"}
      </p>
      <h2 className="mt-3 text-[11pt] font-bold">Files ({order.files.length})</h2>
      <ol className="list-decimal columns-2 gap-6 pl-5 text-[9pt]">
        {files.map((f) => (
          <li key={f.id} className="break-inside-avoid truncate">
            {f.name}
          </li>
        ))}
      </ol>
      {more > 0 && <p className="text-[9pt]">…and {more} more (see the order online).</p>}
    </div>
  );
}

interface CustomerRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  verified: boolean;
  status: string;
  companies: string[];
  created_at: string;
}

export function StaffCustomersPage() {
  const { data: config } = useConfig();
  const tz = config?.business_timezone ?? "UTC";
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: ["staff-customers", q, page],
    queryFn: () =>
      api.get<Page<CustomerRow>>(
        `/staff/customers?${new URLSearchParams({ q, page: String(page) })}`,
      ),
  });
  return (
    <div>
      <PageHeader title="Customers" />
      <div className="mb-4 max-w-sm">
        <TextField
          label="Search"
          placeholder="Name or email"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
      </div>
      <DataTable
        caption="Customers"
        loading={query.isLoading}
        rows={query.data?.items}
        rowKey={(c) => c.id}
        page={page}
        pageSize={25}
        total={query.data?.total}
        onPage={setPage}
        columns={[
          { key: "name", header: "Name", render: (c) => c.name },
          { key: "email", header: "Email", render: (c) => c.email },
          { key: "phone", header: "Phone", render: (c) => c.phone ?? "" },
          {
            key: "verified",
            header: "Email confirmed",
            render: (c) => (c.verified ? "Yes" : <Badge tone="warn">No</Badge>),
          },
          { key: "joined", header: "Joined", render: (c) => formatDate(c.created_at, tz) },
        ]}
      />
    </div>
  );
}
